import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * Modal dialog on top of the app. Focus moves into the dialog and returns to the previously
 * focused element on close; Escape closes; Tab is kept inside.
 */
export function Dialog({
  title,
  onClose,
  children,
  className,
  labelledBy,
}: {
  title: string;
  onClose(): void;
  children: ReactNode;
  className?: string;
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    // nothing to focus yet (content still loading): the dialog itself, so Escape works (OIL-209)
    const target =
      el?.querySelector<HTMLElement>('[data-autofocus]') ??
      el?.querySelector<HTMLElement>(
        'input,button,select,textarea,[tabindex]:not([tabindex="-1"])',
      ) ??
      el;
    target?.focus();
    return () => {
      // Restore focus only if it is still ours (not moved into a dialog opened meanwhile).
      const now = document.activeElement;
      if (!now || now === document.body || el?.contains(now)) prev?.focus?.();
    };
  }, []);
  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog-level Escape / focus trap */}
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={labelledBy ? undefined : title}
        aria-labelledby={labelledBy}
        className={`dialog ${className ?? ''}`}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          } else if (e.key === 'Tab') {
            const f = [
              ...(ref.current?.querySelectorAll<HTMLElement>(
                'input,button,select,textarea,[tabindex]:not([tabindex="-1"])',
              ) ?? []),
            ].filter((x) => !x.hasAttribute('disabled'));
            if (f.length === 0) return;
            const first = f[0]!;
            const last = f[f.length - 1]!;
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }}
      >
        {children}
      </div>
    </div>
  );
}
